import { Button, Dialog, DialogProps, Icon, Text } from 'components/ui';
import { getWeightValue } from 'components/ui/listing-fields/expected-weight';
import { format } from 'date-fns';
import { ListingTypeEnum } from 'lib/api/generated';
import { getLivestockTypeName } from 'lib/helpers';
import TwitterShareCustomButton from 'lib/react-share-twitter/react-share-twitter-android';
import { rgba } from 'polished';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { browserName } from 'react-device-detect';
import { FaCopy, FaFacebook } from 'react-icons/fa';
import { IoLogoWhatsapp } from 'react-icons/io';
import { MdEmail } from 'react-icons/md';
import { RiMessage3Fill } from 'react-icons/ri';
import { FacebookShareButton, WhatsappShareButton } from 'react-share';
import { toast } from 'react-toastify';
import { useMedia } from 'react-use';
import { DATE_FORMAT } from 'settings/config';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;
  padding: 40px;
  @media screen and (max-width: ${breakpoints.medium}) {
    margin-top: 15px;
    padding: 0px;
  }
`;

const ActionButton = styled(Button)`
  width: 200px;
  height: 44px;
`;

interface ShareListingDialogProps
  extends Pick<DialogProps, 'isOpen' | 'onDismiss'> {
  shareUrl: string;
  listingDetails?: any;
}

const MessageShareLink = styled.a`
  text-decoration: none;
`;

const ShareListingDialog: FC<ShareListingDialogProps> = ({
  isOpen,
  onDismiss,
  shareUrl,
  listingDetails,
}) => {
  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`, false);

  const getStockWeaningDate = (
    product: string,
    form_data?: {
      [x: string]: any;
    }
  ) => {
    if (product === 'GT' && form_data?.goat_weaning_date) {
      return typeof form_data?.goat_weaning_date === 'string'
        ? form_data?.goat_weaning_date
        : form_data?.goat_weaning_date?.join(', ');
    } else if (product === 'SH' && form_data?.sheep_weaning_date) {
      return typeof form_data?.sheep_weaning_date === 'string'
        ? form_data?.sheep_weaning_date
        : form_data?.sheep_weaning_date?.join('/');
    } else if (product === 'CT' && form_data?.cattle_weaning_date) {
      return typeof form_data?.cattle_weaning_date === 'string'
        ? form_data?.cattle_weaning_date
        : form_data?.cattle_weaning_date?.join(', ');
    }
  };

  const [message, setMessage] = useState<string>('');
  const [emailBody, setEmailBody] = useState<string>('');

  const getGenderValue = (product: string) => {
    if (product === 'GT') {
      return (
        Array.isArray(listingDetails?.form_data?.goat_gender) &&
        listingDetails?.form_data?.goat_gender?.join(', ')
      );
    } else if (product === 'SH') {
      return (
        Array.isArray(listingDetails?.form_data?.sheep_gender) &&
        listingDetails?.form_data?.sheep_gender?.join(', ')
      );
    } else if (product === 'CT') {
      return (
        Array.isArray(listingDetails?.form_data?.cattle_gender) &&
        listingDetails?.form_data?.cattle_gender?.join(', ')
      );
    }
  };

  const getShareUrl = useCallback(
    (source: string) => {
      if (!shareUrl) return shareUrl;

      const utmParams = new URLSearchParams({
        utm_source: source,
        utm_medium: 'social_share',
        utm_content: 'web_portal',
        utm_campaign: 'listing_share',
      });

      const hasQuestionMark = shareUrl.includes('?');
      return `${shareUrl}${hasQuestionMark ? '&' : '?'}${utmParams.toString()}`;
    },
    [shareUrl]
  );

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareUrl('copy_link'));
    toast.success('Link copied!');
  };

  const shareListing = (e: any) => {
    e.stopPropagation();
  };

  const isIOS = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  };

  const handleFacebookShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const iosShareUrl = getShareUrl('facebook');
    const shareText = `${message}\n\n${iosShareUrl}`;

    if (navigator.share && isIOS()) {
      navigator
        .share({
          title: listingTitle,
          text: shareText,
          url: iosShareUrl,
        })
        .catch((error) => {
          fallbackFacebookShare();
        });
    } else {
      fallbackFacebookShare();
    }
  };

  const fallbackFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      getShareUrl('facebook')
    )}&quote=${encodeURIComponent(message)}`;
    const windowFeatures =
      'width=600,height=400,menubar=no,toolbar=no,resizable=yes,scrollbars=yes';

    window.open(facebookUrl, 'facebookShare', windowFeatures);
  };

  const stock_age = useMemo(() => {
    let formatted_age: string = '';
    if (listingDetails) {
      const stock_age =
        getLivestockTypeName(listingDetails.product).toLowerCase() +
        '_stock_ages';
      if (
        listingDetails.form_data[stock_age] &&
        listingDetails.form_data[stock_age].length
      ) {
        formatted_age += listingDetails.form_data[stock_age].join(', ') + ' ';
      }

      const hasLambDrop =
        listingDetails.form_data?.lamb_drop &&
        listingDetails.form_data?.lamb_drop.length > 0;
      const hasWeaningDate = !!getStockWeaningDate(
        listingDetails.product,
        listingDetails.form_data
      );

      if (hasLambDrop || hasWeaningDate) {
        formatted_age += '(';
        if (hasLambDrop) {
          formatted_age += Array.isArray(listingDetails?.form_data?.lamb_drop)
            ? listingDetails?.form_data?.lamb_drop?.join('/')
            : listingDetails?.form_data?.lamb_drop + ' drop';
        }

        if (hasWeaningDate) {
          formatted_age +=
            ', weaned ' +
            getStockWeaningDate(
              listingDetails.product,
              listingDetails.form_data
            );
        }
        formatted_age += ')';
      }

      return formatted_age;
    } else {
      return '';
    }
  }, [listingDetails]);

  const stock_gender = useMemo(() => {
    let formatted_gender: string = '';
    if (listingDetails) {
      const stock_gender =
        getLivestockTypeName(listingDetails.product).toLowerCase() + '_gender';
      const pregnancy_status =
        getLivestockTypeName(listingDetails.product).toLowerCase() +
        '_pregnancy_status';
      if (
        listingDetails.form_data[stock_gender] &&
        listingDetails.form_data[stock_gender].length
      ) {
        formatted_gender +=
          listingDetails.form_data[stock_gender].join(', ') + ' ';
      }

      if (
        listingDetails.form_data[pregnancy_status] &&
        listingDetails.form_data[pregnancy_status].length
      ) {
        if (
          listingDetails.form_data[stock_gender] &&
          listingDetails.form_data[stock_gender].length
        ) {
          formatted_gender +=
            '(' + listingDetails.form_data[pregnancy_status].join(', ') + ')';
        } else {
          formatted_gender +=
            listingDetails.form_data[pregnancy_status].join(', ');
        }
      }
      return formatted_gender;
    } else {
      return '';
    }
  }, [listingDetails]);

  const stock_weight = useMemo(() => {
    return getWeightValue({
      weightMin: listingDetails.form_data?.weight_expected_min,
      weightAvg: listingDetails.form_data?.weight_expected_avg,
      weightMax: listingDetails.form_data?.weight_expected_max,
    });
  }, [listingDetails]);

  const listingTitle = useMemo(() => {
    let listingTitle = '';
    if (listingDetails.type === ListingTypeEnum.Sell) {
      listingTitle = !listingDetails.sold
        ? `For Sale: ${listingDetails.title} \n`
        : `Filled: ${listingDetails.title} \n`;
    }
    if (listingDetails.type === ListingTypeEnum.Buy) {
      listingTitle = !listingDetails.sold
        ? `Wanted: ${listingDetails.title} \n`
        : `Bought: ${listingDetails.title} \n`;
    }
    return listingTitle;
  }, [listingDetails]);

  useEffect(() => {
    let mailMessage = '';
    let socialMessage = '';

    mailMessage = listingTitle;
    socialMessage = listingTitle;

    if (listingDetails.sold) {
      mailMessage += `Transaction Date : ${format(
        new Date(listingDetails?.sold),
        DATE_FORMAT.listing
      )} \n`;
    }

    if (listingDetails.quantity_label) {
      mailMessage += `Quantity: ${listingDetails.quantity_label} \n`;
      socialMessage += `Quantity: ${listingDetails.quantity_label} \n`;
    }
    if (listingDetails.form_data.breed) {
      const breedPrefix = 'Breed: ';
      if (listingDetails.form_data?.breed === 'Purebred') {
        mailMessage +=
          breedPrefix +
          listingDetails.form_data?.breed +
          ' - ' +
          listingDetails.form_data?.purebred_breed +
          '\n';
        socialMessage +=
          breedPrefix +
          listingDetails.form_data?.breed +
          ' - ' +
          listingDetails.form_data?.purebred_breed +
          '\n';
      } else if (listingDetails.form_data?.breed.includes('Crossbred')) {
        if (typeof listingDetails.form_data?.mixed_breed === 'string') {
          mailMessage +=
            breedPrefix +
            listingDetails.form_data?.breed +
            ' - ' +
            listingDetails.form_data?.mixed_breed +
            '\n';
          socialMessage +=
            breedPrefix +
            listingDetails.form_data?.breed +
            ' - ' +
            listingDetails.form_data?.mixed_breed +
            '\n';
        } else {
          const breedArr = listingDetails.form_data?.mixed_breed?.map(
            (item: { composition: number; name?: string }) =>
              `${item?.composition}% ${item?.name}`
          );
          mailMessage +=
            breedPrefix +
            listingDetails.form_data?.breed +
            ' - ' +
            breedArr.join(', ') +
            '\n';
          socialMessage +=
            breedPrefix +
            listingDetails.form_data?.breed +
            ' - ' +
            breedArr.join(', ') +
            '\n';
        }
      } else {
        mailMessage += `${breedPrefix} ${listingDetails.form_data.breed}\n`;
      }
    }
    if (stock_age) {
      mailMessage += `Age: ${stock_age}\n`;
      socialMessage += `Age: ${stock_age}\n`;
    }
    if (stock_gender) {
      mailMessage += `Gender: ${stock_gender}\n`;
      socialMessage += `Gender: ${stock_gender}\n`;
    }
    if (stock_weight) {
      mailMessage += `Weight: ${stock_weight}\n`;
      socialMessage += `Weight: ${stock_weight}\n`;
    }
    if (listingDetails.listing_address) {
      const { suburb, state, postcode } = listingDetails.listing_address;
      if (suburb || state || postcode) {
        mailMessage += `Location: ${suburb ? `${suburb}` : ''}${
          state ? `, ${state}` : ''
        }${postcode ? ` ${postcode}` : ''}\n`;
        socialMessage += `Location: ${suburb ? `${suburb}` : ''}${
          state ? `, ${state}` : ''
        }${postcode ? ` ${postcode}` : ''}\n`;
      }
    }
    if (listingDetails.form_data.comments) {
      mailMessage += `Other comments: ${listingDetails.form_data.comments}\n`;
      socialMessage += `Other comments: ${listingDetails.form_data.comments}\n`;
    }
    if (listingDetails.prices_label) {
      mailMessage += `Price: ${listingDetails.prices_label}\n`;
      socialMessage += `Price: ${listingDetails.prices_label}\n`;
    }
    if (shareUrl) {
      mailMessage += `Further info and images: ${getShareUrl('email')}`;
    }
    setMessage(socialMessage);
    setEmailBody(mailMessage);
  }, [
    listingDetails,
    stock_age,
    stock_gender,
    stock_weight,
    shareUrl,
    getShareUrl,
    listingTitle,
  ]);

  return (
    <Dialog
      title={'Share Listing'}
      isOpen={isOpen}
      onDismiss={onDismiss}
      overlayBackgroundColour={rgba(0, 0, 0, 0.3)}
      hasBlurredOverlay={true}
    >
      <ButtonContainer>
        {/* Facebook Share - Custom implementation for iOS compatibility */}
        {isIOS() ? (
          <ActionButton
            size='small'
            variant='outline'
            type='button'
            onClick={handleFacebookShare}
          >
            <Text>Share</Text>
          </ActionButton>
        ) : (
          <FacebookShareButton
            url={getShareUrl('facebook')}
            quote={message}
            hashtag={'#agoralivestockmarkets'}
            onClick={(e: any) => {
              shareListing(e);
            }}
          >
            <ActionButton size='small' variant='outline' type='button'>
              <FaFacebook />
              <Text>Share in Facebook</Text>
            </ActionButton>
          </FacebookShareButton>
        )}

        <TwitterShareCustomButton
          url={getShareUrl('twitter')}
          title={
            'I have ' +
            listingDetails?.quantity_label +
            ' ' +
            getGenderValue(listingDetails?.product) +
            ' for sale on @AgoraLivestock Markets. Info available at '
          }
          hashtags={['agoralivestockmarkets']}
          related={['@AgoraLivestock']}
          onClick={(e: any) => {
            shareListing(e);
          }}
        >
          <ActionButton size='small' variant='outline' type='button'>
            <Icon component='XIcon' />
            <Text maxWidth={'max-content'}>Share in X</Text>
          </ActionButton>
        </TwitterShareCustomButton>

        {browserName === 'WebKit' ? (
          <a
            href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
              `${message}\n${getShareUrl('whatsapp')}`
            )}`}
          >
            <ActionButton size='small' variant='outline' type='button'>
              <IoLogoWhatsapp /> <Text>Share in WhatsApp</Text>
            </ActionButton>
          </a>
        ) : (
          <WhatsappShareButton
            url={getShareUrl('whatsapp')}
            onClick={(e: any) => {
              shareListing(e);
            }}
            title={message}
          >
            <ActionButton size='small' variant='outline' type='button'>
              <IoLogoWhatsapp /> <Text>Share in WhatsApp</Text>
            </ActionButton>
          </WhatsappShareButton>
        )}

        {isMobile && (
          <>
            <MessageShareLink
              href={`sms:?&body=${encodeURIComponent(
                `${getShareUrl('sms')}\n${message}`
              )}`}
            >
              <ActionButton size='small' variant='outline' type='button'>
                <RiMessage3Fill />
                Send SMS
              </ActionButton>
            </MessageShareLink>
          </>
        )}

        <>
          <MessageShareLink
            href={`mailto:?subject=${encodeURIComponent(
              `${listingTitle}`
            )}&body=${encodeURIComponent(emailBody)}`}
            target='_blank'
          >
            <ActionButton size='small' variant='outline' type='button'>
              <MdEmail />
              Send Email
            </ActionButton>
          </MessageShareLink>
        </>

        <ActionButton
          size='small'
          variant='outline'
          type='button'
          onClick={handleCopyLink}
        >
          <FaCopy />
          Copy Link
        </ActionButton>
      </ButtonContainer>
    </Dialog>
  );
};

export default ShareListingDialog;
